import type { ReactNode } from "react";
import type { TeacherCommentTones } from "../../../constants/StudentResultCommentTones";
import StudentResultTeacherComment, { getHasTeacherComment } from "./StudentResultTeacherComment";

interface StudentResultAnswerRowProps {
    isLast: boolean;
    /** Off when the body draws its own cell padding, so a divider inside can span the full row. */
    hasPadding?: boolean;
    /** Off when the body is a card of its own, which needs no line to the next one. */
    hasSeparator?: boolean;
    /** The teacher's comment on this answer, shown as a bubble below the body where one exists. */
    comment?: string;
    /** Colours the comment bubble; set together with the comment. */
    tone?: TeacherCommentTones;
    /** A score column laid out beside the body, so the comment stays aligned with the answers, not it. */
    score?: ReactNode;
    children: ReactNode;
}

/**
 * One answer in an exercise's list, separated from the next by a line. The teacher's comment sits in
 * its own line below the body, pushed to the answer's side and capped at the design's width.
 */
const StudentResultAnswerRow = (props: StudentResultAnswerRowProps) => {
    const hasPadding = props.hasPadding ?? true;
    const hasSeparator = (props.hasSeparator ?? true) && !props.isLast;

    // The wrapper's spacing must not render around an empty bubble, so the guard sits here too.
    const renderComment = () => {
        if (!props.tone || !getHasTeacherComment(props.comment)) return null;
        return (
            <div className={`mt-s ${hasPadding ? "" : "px-s pb-s"}`}>
                <div className="max-w-[600px] ml-auto">
                    <StudentResultTeacherComment
                        comment={props.comment}
                        tone={props.tone}
                    />
                </div>
            </div>
        );
    };

    const renderBody = () => {
        const content = (
            <>
                {props.children}
                {renderComment()}
            </>
        );
        if (!props.score) return content;
        return (
            <div className="flex items-stretch gap-s">
                <div className="flex flex-col flex-1 min-w-[0px]">{content}</div>
                {props.score}
            </div>
        );
    };

    return (
        <li
            className={`flex flex-col ${hasPadding ? "py-s" : ""} ${
                hasSeparator ? "border-b border-p6-grey-light" : ""
            }`}
            data-testid="student-result-answer-row"
        >
            {renderBody()}
        </li>
    );
};

export default StudentResultAnswerRow;
